/**
 * CategoryDetailsTable Component
 * Renders detailed time category parameters as a single multi-column grid,
 * with one column per active category (Jahr, Monat, Woche, Tag, Feiertag).
 * Design mirrors CSVFilesTable for visual consistency with input/output files.
 */

import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { styles } from '../styles';
import { CategoryData } from '../types';

interface CategoryColumn {
  label: string;
  data: CategoryData;
}

interface CategoryDetailsTableProps {
  categories: CategoryColumn[];
}

export const CategoryDetailsTable: React.FC<CategoryDetailsTableProps> = ({ categories }) => {
  const { t } = useTranslation('pdfReport');
  const { t: tc } = useTranslation('common');

  if (categories.length === 0) return null;

  const formatBoolean = (val: unknown): string =>
    val ? tc('common.yes') : tc('common.no');

  const formatValue = (val: unknown): string =>
    val === null || val === undefined || val === '' ? '-' : String(val);

  const showCountry = categories.some((c) => c.data.land);

  const rows: { label: string; render: (data: CategoryData) => string }[] = [
    ...(showCountry
      ? [{ label: t('category.country'), render: (d: CategoryData) => formatValue(d.land) }]
      : []),
    {
      label: t('category.detailedCalculation'),
      render: (d: CategoryData) => formatBoolean(d.detaillierteBerechnung),
    },
    { label: t('category.shape'), render: (d: CategoryData) => formatValue(d.datenform) },
    { label: t('category.horizonStart'), render: (d: CategoryData) => formatValue(d.zeithorizontStart) },
    { label: t('category.horizonEnd'), render: (d: CategoryData) => formatValue(d.zeithorizontEnd) },
    { label: t('category.scaling'), render: (d: CategoryData) => formatValue(d.skalierung) },
    { label: t('category.scalingMin'), render: (d: CategoryData) => formatValue(d.skalierungMin) },
    { label: t('category.scalingMax'), render: (d: CategoryData) => formatValue(d.skalierungMax) },
  ];

  const isLastColumn = (index: number) => index === categories.length - 1;

  return (
    <View style={styles.csvTable}>
      {/* Header Row */}
      <View style={styles.tableRow}>
        <View style={styles.csvTableColHeaderFirst}>
          <Text style={styles.csvTableCellHeader}>{t('csvTable.parameter')}</Text>
        </View>
        {categories.map((category, colIndex) => (
          <View
            key={`cat-header-${colIndex}`}
            style={[
              styles.csvTableColHeader,
              isLastColumn(colIndex) ? styles.csvTableColHeaderLast : {},
            ]}
          >
            <Text style={styles.csvTableCellHeader}>{category.label}</Text>
          </View>
        ))}
      </View>

      {/* Parameter Rows */}
      {rows.map((row, rowIndex) => (
        <View
          key={`cat-row-${rowIndex}`}
          style={[
            styles.tableRow,
            rowIndex % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
          ]}
        >
          <View style={styles.csvTableColFirst}>
            <Text style={styles.csvTableCellLabel}>{row.label}</Text>
          </View>
          {categories.map((category, colIndex) => (
            <View
              key={`cat-value-${rowIndex}-${colIndex}`}
              style={[
                styles.csvTableCol,
                rowIndex % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                isLastColumn(colIndex) ? styles.csvTableColLast : {},
              ]}
            >
              <Text style={styles.csvTableCell}>{row.render(category.data)}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};
